import moment from 'moment-timezone';

import { CalendarTile, DISCIPLINE_COLOR_PALETTE, MAX_WINDOW_EVENTS, ROW_HEIGHT } from './types';

export const formatTime = (date: Date | null, timezone: string, format?: string): string => {
    if (!date) {
        return '';
    }
    return moment(date)
        .tz(timezone)
        .format(format ?? 'h:mmA');
};

export const formatTimeRange = (
    startDate: Date | null,
    endDate: Date | null,
    format?: string,
): string => {
    if (!startDate || !endDate) {
        return '';
    }
    return `${moment(startDate).format(format ?? 'h:mmA')} to ${moment(endDate).format(
        format ?? 'h:mmA',
    )}`;
};

/**
 * Group visit tiles by day (keyed by start-of-day ISO string).
 */
export const groupTilesByDay = (tiles: CalendarTile[]): Map<string, CalendarTile[]> => {
    const grouped = new Map<string, CalendarTile[]>();

    tiles.forEach((tile) => {
        const dayKey = moment(tile.start).startOf('day').toISOString();
        const dayGroup = grouped.get(dayKey) ?? [];
        dayGroup.push(tile);
        grouped.set(dayKey, dayGroup);
    });

    return grouped;
};

export type VisibleEventsResult = {
    visible: CalendarTile[];
    hidden: CalendarTile[];
    needsOverflow: boolean;
};

/**
 * Always shows exactly MAX_WINDOW_EVENTS visit events, adds overflow if there are more than MAX_WINDOW_EVENTS
 */
export const determineVisibleEvents = (visitEvents: CalendarTile[]): VisibleEventsResult => {
    const sortedVisitEvents = [...visitEvents].sort((a, b) => {
        const timeA = a.start.getTime();
        const timeB = b.start.getTime();
        return timeA - timeB;
    });

    const visitEventCount = sortedVisitEvents.length;

    if (visitEventCount > MAX_WINDOW_EVENTS) {
        return {
            visible: sortedVisitEvents.slice(0, MAX_WINDOW_EVENTS),
            hidden: sortedVisitEvents.slice(MAX_WINDOW_EVENTS),
            needsOverflow: true,
        };
    }

    return {
        visible: sortedVisitEvents,
        hidden: [],
        needsOverflow: false,
    };
};

/**
 * Build the list of month anchor dates (one per month) spanning [start, end] inclusive.
 * The calendar renders one RBC month per entry.
 */
export const getMonthsBetween = (start: Date, end: Date): Date[] => {
    const months: Date[] = [];
    const startMonth = moment(start).startOf('month');
    const endMonth = moment(end).startOf('month');
    const current = startMonth.clone();

    while (current.isSameOrBefore(endMonth, 'month')) {
        months.push(current.toDate());
        current.add(1, 'month');
    }

    return months;
};

/**
 * Some months have more weeks than others. By default, RBC library will force the calendar to fit the
 * height of the container which then compresses the day cells.
 * In order to get the height of the month the same, we get the number of weeks and adjust based on that.
 */
export const getMonthHeight = (monthDate: Date) => {
    const monthMoment = moment(monthDate);
    const numWeeks = Math.ceil(
        (monthMoment.daysInMonth() + monthMoment.startOf('month').day()) / 7,
    );
    return numWeeks * ROW_HEIGHT;
};

export const getDisciplineColor = (code: string): string => {
    let hash = 0;
    for (let i = 0; i < code.length; i++) {
        hash = code.charCodeAt(i) + ((hash << 5) - hash);
    }
    return DISCIPLINE_COLOR_PALETTE[Math.abs(hash) % DISCIPLINE_COLOR_PALETTE.length];
};

/**
 * Assigns a color to each discipline by index so the full palette is exhausted
 * before any color repeats. Pass all known disciplines in a stable order.
 */
export const buildDisciplineColorMap = (disciplines: { code: string }[]): Map<string, string> => {
    const map = new Map<string, string>();
    disciplines.forEach((d, i) => {
        map.set(d.code, DISCIPLINE_COLOR_PALETTE[i % DISCIPLINE_COLOR_PALETTE.length]);
    });
    return map;
};
